import styled from 'styled-components';
import {
  IconAttachmentClose,
  IconAttachmentDownload,
  IconAttachmentNext,
  IconAttachmentPrevious,
  IconAttachmentPrint,
} from './icons';

export const AttachmentIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--color-text-dark, var(--color-text-base, #042a2a));
  line-height: 0;

  &:focus-visible {
    outline: 2px solid var(--color-primary, #41aaaf);
    outline-offset: 2px;
    border-radius: 4px;
  }

  &:hover svg {
    transform: scale(1.2);
  }

  &:active svg {
    transform: scale(1);
  }

  svg {
    display: block;
    transition: all 0.2s ease-in-out;
  }
`;

export const StyledIconClose = styled(IconAttachmentClose)`
  width: 20px;
  height: 20px;
  color: inherit;
`;

export const StyledIconPrevious = styled(IconAttachmentPrevious)`
  height: 16px;
  color: inherit;
`;

export const StyledIconNext = styled(IconAttachmentNext)`
  height: 16px;
  color: inherit;
`;

export const StyledIconDownload = styled(IconAttachmentDownload)`
  width: 24px;
  height: 24px;
  color: inherit;
`;

export const StyledIconPrint = styled(IconAttachmentPrint)`
  width: 24px;
  height: 24px;
  color: inherit;
`;

export const AttachmentViewerBackdrop = styled.div<{ $zIndex?: number }>`
  position: fixed;
  inset: 0;
  cursor: pointer;
  background: var(--color-bg-mask, rgba(4, 42, 42, 0.35));
  z-index: ${({ $zIndex }) => ($zIndex ?? 1300) - 1};
`;

export const AttachmentViewerWrapper = styled.div<{
  $menuWidth?: number;
  $drawerOffset?: number;
  $zIndex?: number;
  $embedded?: boolean;
  $standaloneWidth?: number;
  $standaloneFullHeight?: boolean;
  $centered?: boolean;
}>`
  background-color: var(
    --color-attachment-viewer-bg,
    var(--color-status-warning-bg, #fff5e2)
  );
  border: 1px solid var(--color-border, #dbeae3);
  display: flex;
  flex-direction: column;
  font-family: var(--font-family, Inter, Arial, sans-serif);
  --av-viewer-width: ${({ $standaloneWidth }) => $standaloneWidth ?? 670}px;
  ${({
    $embedded,
    $menuWidth,
    $drawerOffset,
    $zIndex,
    $standaloneFullHeight,
    $centered,
  }) => {
    if (!$embedded) {
      if ($centered) {
        return `
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(var(--av-viewer-width), calc(100vw - 48px));
  max-width: calc(100vw - 48px);
  height: calc(100vh - 64px);
  max-height: calc(100vh - 64px);
  z-index: ${$zIndex ?? 1300};
  box-sizing: border-box;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(4, 42, 42, 0.18);
  `;
      }
      return `
  position: fixed;
  top: 135px;
  right: ${$drawerOffset || 0}px;
  width: var(--av-viewer-width);
  max-width: calc(100vw - ${$menuWidth || 0}px - ${$drawerOffset || 0}px);
  height: calc(100vh - 79px);
  max-height: calc(100vh - 79px);
  z-index: ${$zIndex ?? 1000};
  box-sizing: border-box;
  `;
    }
    if ($standaloneFullHeight) {
      return `
  position: relative;
  width: 100%;
  margin-top: 0;
  margin-bottom: 0;
  border-radius: 8px;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 0 0 auto;
  height: auto;
  min-height: 500px;
  max-height: calc(100vh - 132px);
  `;
    }
    return `
  position: relative;
  width: 100%;
  min-height: 410px;
  max-height: 610px;
  margin-top: -30px;
  border-radius: 8px;
  overflow: hidden;
  .av-preview-area {
    margin-bottom: 0;
  }
  `;
  }}
  .av-centered-content {
    text-align: center;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
  .av-transaction-reference {
    font-family: var(--font-family, Inter, Arial, sans-serif);
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0;
    text-align: center;
    margin-bottom: 8px;
    max-width: 420px;
    margin-left: auto;
    margin-right: auto;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--color-text-dark, #18302b);
  }
  .av-no-attachment-text {
    color: var(--color-text-muted, #5a6a6a);
    font-family: var(--font-family, Inter, Arial, sans-serif);
    font-weight: 500;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0;
    text-align: center;
    margin-bottom: 32px;
  }
  .av-top-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    height: 48px;
    padding: 0 16px;
    background: var(--color-surface-raised, #fff);
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    border-bottom: 1px solid var(--color-border, #e5e5e5);
    width: 100%;
    box-sizing: border-box;
    flex-shrink: 0;
    position: relative;
    color: var(--color-text-base, #2e2e2e);
  }
  .av-top-bar-left {
    font-family: var(--font-family, Inter, sans-serif);
    font-weight: 600;
    font-size: 12px;
    line-height: 24px;
    letter-spacing: 0;
    color: var(--color-text-base, #2e2e2e);
    min-width: 0;
    flex: 1 1 auto;
  }
  .av-file-info {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
  .av-file-name {
    font-weight: 600;
    font-size: 14px;
    color: var(--color-text-base, #2e2e2e);
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
    min-width: 0;
  }
  .av-top-bar-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }
  .av-previewing-text {
    font-family: var(--font-family, Inter, Arial, sans-serif);
    font-weight: 550;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0;
    color: var(--color-text-dark, #042a2a);
    opacity: 0.7;
    white-space: nowrap;
  }
  .av-toolbar {
    background: var(--color-primary, #41aaaf);
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 48px;
    padding: 0 16px;
    color: var(--color-attachment-viewer-toolbar-fg, #ffffff);
    font-size: 15px;
    gap: 16px;
    width: 100%;
    box-sizing: border-box;
    flex-shrink: 0;
  }
  .av-toolbar-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 0;
    font-weight: 600;
    font-size: 18px;
    cursor: pointer;
    width: 32px;
    height: 32px;
    line-height: 0;
    flex-shrink: 0;
    color: var(--color-attachment-viewer-toolbar-fg, #ffffff);
  }
  .av-toolbar-svg {
    width: 24px;
    height: 24px;
    display: block;
    color: var(--color-attachment-viewer-toolbar-fg, #ffffff);
  }
  .av-toolbar-icon-group {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }
  .av-toolbar-icon-group > span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
  }
  .av-preview-area {
    flex: 0 1 auto;
    background: var(--color-surface-raised, #fff);
    margin: 16px;
    border-radius: 10px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    width: calc(100% - 32px);
    min-height: 200px;
    max-height: calc(100vh - 270px);
  }
  .av-pdf-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0;
    box-sizing: border-box;
    background-color: var(--color-surface-raised, #fff);
    min-height: 200px;
    max-height: calc(100vh - 270px);
    .react-pdf__Page {
      margin-bottom: 0;
      border-bottom: 10px solid var(--color-border, #f0f0f0);
      &:last-child {
        border-bottom: none;
      }
    }
    .react-pdf__Page__canvas {
      max-width: 100%;
      height: auto !important;
    }
  }
  .av-pdf-zoom-container .react-pdf__Page__canvas {
    max-width: none;
  }
  ${({ $embedded, $standaloneFullHeight }) =>
    $embedded && $standaloneFullHeight
      ? `
  .av-centered-content {
    height: auto !important;
    min-height: 160px;
    padding: 24px 16px;
  }
  .av-top-bar,
  .av-toolbar {
    flex-shrink: 0;
  }
  .av-preview-area {
    flex: 1 1 auto !important;
    min-height: calc(500px - 112px) !important;
    max-height: calc(100vh - 228px) !important;
    height: auto !important;
    margin: 8px !important;
    width: calc(100% - 16px) !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .av-pdf-container {
    flex: 0 0 auto !important;
    min-height: 0 !important;
    max-height: calc(100vh - 244px) !important;
    height: auto !important;
    width: 100% !important;
    align-items: flex-start !important;
    justify-content: flex-start !important;
    overflow-x: auto !important;
    overflow-y: auto !important;
    scrollbar-gutter: stable;
    scrollbar-width: thin;
    overscroll-behavior: contain;
  }
  .av-pdf-container .av-pdf-zoom-container {
    flex: 0 0 auto !important;
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
  }
  `
      : ''}
`;
